import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import { FiHeart, FiGift, FiInfo, FiX } from 'react-icons/fi';
import '../../styles/ThinkTankNotice.css';

/**
 * Set by both login paths (LoginPage and the /hubvalidation SSO flow) right after
 * auth succeeds. localStorage, not state, because /hubvalidation finishes with a
 * full-page redirect. Cleared on dismiss, so the notice shows once per login —
 * not on every refresh.
 */
export const THINK_TANK_NOTICE_FLAG = 'showThinkTankNotice';

export function markThinkTankNoticePending(): void {
    try {
        localStorage.setItem(THINK_TANK_NOTICE_FLAG, '1');
    }
    catch { }
}

function isNoticePending(): boolean {
    try {
        return localStorage.getItem(THINK_TANK_NOTICE_FLAG) === '1';
    }
    catch {
        return false;
    }
}

// Agencies that must NOT see the notice (comma-separated NowCerts agency ids, env var
// REACT_APP_ThinkTank_Notice_Excluded_AgencyIds). Empty → everyone sees it.
const EXCLUDED_AGENCY_IDS = (process.env.REACT_APP_ThinkTank_Notice_Excluded_AgencyIds || '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

function isAgencyExcluded(): boolean {
    if (EXCLUDED_AGENCY_IDS.length === 0) return false;
    try {
        const agencyId = (localStorage.getItem('NCAgencyId') || '').trim().toLowerCase();
        return !!agencyId && EXCLUDED_AGENCY_IDS.includes(agencyId);
    }
    catch {
        return false;
    }
}

interface ThinkTankNoticeProps {
    /** True once the user is signed in and on an in-app page (not login/SSO screens). */
    active: boolean;
    /** Re-check the flag on navigation — LoginPage sets it, then routes client-side. */
    pathname: string;
}

const ThinkTankNotice: React.FC<ThinkTankNoticeProps> = ({ active, pathname }) => {
    const [open, setOpen] = useState(false);
    const confirmRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!active || !isNoticePending()) return;
        if (isAgencyExcluded()) {
            // Checked at show time — NCAgencyId is written during login, after the flag.
            try { localStorage.removeItem(THINK_TANK_NOTICE_FLAG); } catch { }
            return;
        }
        setOpen(true);
    }, [active, pathname]);

    const dismiss = useCallback(() => {
        try {
            localStorage.removeItem(THINK_TANK_NOTICE_FLAG);
        }
        catch { }
        setOpen(false);
    }, []);

    useEffect(() => {
        if (!open) return;
        confirmRef.current?.focus();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') dismiss();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, dismiss]);

    return (
        <SafeAnimatePresence>
            {open && (
                <motion.div
                    className="ttn-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                >
                    <motion.div
                        className="ttn-modal"
                        role="alertdialog"
                        aria-modal="true"
                        aria-labelledby="ttn-title"
                        aria-describedby="ttn-body"
                        initial={{ opacity: 0, y: 16, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 8, scale: 0.98 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <button type="button" className="ttn-close" onClick={dismiss} aria-label="Close">
                            <FiX size={16} />
                        </button>

                        <div className="ttn-icon">
                            <FiHeart size={20} />
                        </div>

                        <h2 id="ttn-title" className="ttn-title">Thank you, Think Tank agencies</h2>

                        <div id="ttn-body" className="ttn-body">
                            <p>
                                We’re grateful you’ve been part of the Think Tank and for how you’ve adopted
                                Insuranceagency.ai. As AI usage has grown, so have the ongoing costs to run it,
                                which means we’ll need to start charging for Insuranceagency.ai going forward.
                            </p>

                            <div className="ttn-offer">
                                <FiGift size={16} className="ttn-offer__icon" />
                                <p>
                                    As a thank-you for being early partners, we’d like to offer you an{' '}
                                    <strong>exclusive promotional deal available only to Think Tank agencies</strong>.
                                    Reach out and we’ll walk through the details together.
                                </p>
                            </div>

                            <div className="ttn-note">
                                <FiInfo size={14} className="ttn-note__icon" />
                                <p>
                                    If we don’t hear from you, no worries. The service will be deactivated in a week
                                    and no charges will be made.
                                </p>
                            </div>
                        </div>

                        <div className="ttn-actions">
                            <button ref={confirmRef} type="button" className="ttn-btn" onClick={dismiss}>
                                Got it
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </SafeAnimatePresence>
    );
};

export default ThinkTankNotice;
